import { safeFetchAPI } from '@/api';
import { log, logger, utils } from '@/helpers';
import { CampaignConversionActionMetaMap } from '@/pages/campaign-builder/campaign.builder.utils';
import { downloadCSV } from '@/pages/surveys/helpers';
import { CampaignChannelPerformanceResponse, CampaignReportFilters, CoMarketingReportFilters, ConversionActionType } from '@/types';
import { create, StoreApi, UseBoundStore } from 'zustand';
import { ChannelPerformanceReportingStore, ChannelPerformanceReportingStoreState } from '../campaign-reports/createCampaignReportChannelPerformanceStore';
import { LOCAL_CAMPAIGN_REPORT_TEST_TOKEN, LOCAL_CAMPAIGN_REPORT_TEST_UID } from '../campaign-reports/createCampaignReportFiltersStore';

type CoMarketingChannelPerformanceReportingStoreState = ChannelPerformanceReportingStoreState & {
	retailerCampaignID?: string;
};

type CoMarketingChannelPerformanceReportingStore = ChannelPerformanceReportingStore & {
	retailerCampaignID?: string;
};

const initialState: CoMarketingChannelPerformanceReportingStoreState = {
	loaded: false,
	loading: false,
	channelPerformance: null,
	campaignID: '',
	retailerCampaignID: '',
};

export const createCoMarketingReportChannelPerformanceStore = (campaignID = '', retailerCampaignID?: string) =>
	create<CoMarketingChannelPerformanceReportingStore>((set, get) => ({
		...initialState,
		campaignID,
		retailerCampaignID,
		fetchChannelPerformance: async (req?: Partial<CampaignReportFilters>) => {
			// If campaignID is not provided or is global, return
			if (!campaignID || campaignID === 'global') {
				return;
			}

			const coMarketingReportFilters: CoMarketingReportFilters = {
				startDate: req?.startDate ?? 0,
				endDate: req?.endDate ?? 0,
				strictAttribution: req?.strictAttribution ?? false,
				retailerCampaignIDs: retailerCampaignID ? [retailerCampaignID] : [],
			};

			set({ loading: true });

			const [response, error] = await safeFetchAPI<CampaignChannelPerformanceResponse>(`/co-marketing/reports/channels/:uid/${campaignID}`, {
				method: 'POST',
				payload: coMarketingReportFilters,
				forceToken: LOCAL_CAMPAIGN_REPORT_TEST_TOKEN,
				uid: LOCAL_CAMPAIGN_REPORT_TEST_UID,
			});

			if (error) {
				utils?.toaster?.addToast?.({
					message: error || 'Error loading campaigns',
					type: 'danger',
				});
				set({ loaded: true, loading: false });
				return;
			}

			logger.log('/co-marketing/reports/channels/:uid').prefix(campaignID).add({ campaignID, retailerCampaignID }).addnl('response', response);

			set({
				loaded: true,
				loading: false,
				channelPerformance: response ?? null,
			});
		},

		downloadCSV: async (type: 'link_clicks' | 'engagements') => {
			set({ loading: true, loaded: false });

			const { channelPerformance } = get();

			type CSVData =
				| {
						Link: string;
						Clicks: number;
				  }
				| {
						Action: string;
						Revenue: number;
						Count: number;
						Percentage: number;
						RawLabel: string;
				  };
			let csvData: CSVData[] = [];
			if (type === 'link_clicks') {
				csvData = Object.entries({ ...channelPerformance?.linkClicks, ...channelPerformance?.dynamicLinkClicks }).map(([link, count]) => ({
					Link: link,
					Clicks: count,
				}));
			} else if (type === 'engagements') {
				csvData =
					channelPerformance?.conversionsByAction?.map((entry) => ({
						Action: CampaignConversionActionMetaMap[entry.label as ConversionActionType]?.name ?? entry.label,
						Revenue: entry.revenue,
						Count: entry.count,
						Percentage: entry.percentage,
						RawLabel: entry.label,
					})) ?? [];
			}

			if (!csvData.length) {
				set({ loading: false, loaded: false });
				utils?.toaster?.addToast?.({
					message: 'No data available for CSV download',
					type: 'warning',
				});
				return;
			}

			// Dynamically generate headers from the first row
			const headers = Object.keys(csvData[0]).map((key) => ({ label: key, key }));
			const date = new Date().toISOString().split('T')[0];
			const campaignID = get().campaignID;
			downloadCSV(`${campaignID}_${type}_${date}.csv`, headers, csvData);

			set({ loading: false, loaded: true });
			utils?.toaster?.addToast?.({
				message: 'CSV download complete',
				type: 'success',
			});
		},

		reset: (filters?: CampaignReportFilters) => {
			set(initialState);
			if (filters) {
				get().fetchChannelPerformance(filters);
			}
		},
	}));

const storeInstances = new Map<string, UseBoundStore<StoreApi<ChannelPerformanceReportingStore>>>();

export const getCoMarketingReportChannelPerformanceStore = (id = 'global', retailerCampaignID?: string) => {
	const key = retailerCampaignID ? `${id}-${retailerCampaignID}` : id;
	if (!storeInstances.has(key)) {
		storeInstances.set(key, createCoMarketingReportChannelPerformanceStore(id, retailerCampaignID));
	}
	return storeInstances.get(key)!;
};
